import { useCallback, useEffect, useState } from "react";
import { FaArrowRotateLeft } from "react-icons/fa6";
import { HiPencilAlt } from "react-icons/hi";
import { LuCopyPlus } from "react-icons/lu";
import { MdPayments } from "react-icons/md";
import { RiDeleteBin5Fill } from "react-icons/ri";
import { TbCoinFilled } from "react-icons/tb";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import ModalCadastroFornecedor from "../../components/modais/ModalCadastroFornecedor";
import ModalCadastroCategoriaFinanceira from "../../components/modais/ModalCadastroCategoriaFinanceira";
import ModalCadastroCentroCusto from "../../components/modais/ModalCadastroCentroCusto";
import ModalCadastroFormaPagamento from "../../components/modais/ModalCadastroFormaPagamento";
import { api } from "../../services/api";
import {
  repositorioCategoriaFinanceira,
  repositorioCategoriaMestra,
  repositorioCentroCusto,
  repositorioContaPagar,
  repositorioFormaPagamento,
  repositorioFornecedor,
  repositorioNotaFiscal,
} from "../../sync/repositories/financeiro";
import { nomePessoa } from "../../utils/nomesPessoa";

// Extrai uma mensagem legível de qualquer formato de erro que a API
// devolve (string, {detail}, {campo: [...]} etc.) — sem isso, erros de
// configuração (ex.: "Desconto não é permitido...") ficavam escondidos
// atrás de um "Erro ao salvar conta." genérico.
const extrairMensagemErro = (error) => {
  const dados = error.response?.data;
  if (!dados) return "Falha na conexão com o servidor.";
  if (typeof dados === "string") return dados;
  if (typeof dados.detail === "string") return dados.detail;
  const mensagens = [];
  const percorrer = (obj) => {
    if (typeof obj === "string") mensagens.push(obj);
    else if (Array.isArray(obj)) obj.forEach(percorrer);
    else if (obj && typeof obj === "object") Object.values(obj).forEach(percorrer);
  };
  percorrer(dados);
  return mensagens.join("\n") || "Erro ao processar a solicitação.";
};

// Pede o usuário autorizador quando uma alteração em conta já paga exige
// autorização de supervisor (configuração de sistema). Reaproveita o
// endpoint de colaboradores já usado em Configurações → Usuários.
const solicitarAutorizacao = async () => {
  try {
    const resp = await api.get("/configuracoes/perfis-acesso/");
    const colaboradores = (resp.data?.results || resp.data || []).filter((c) => c.ativo);
    if (colaboradores.length === 0) {
      alert("Nenhum colaborador disponível para autorizar esta operação.");
      return null;
    }
    const lista = colaboradores.map((c) => `${c.usuario} - ${c.nome_completo || c.username}`).join("\n");
    const escolha = window.prompt(
      `Esta alteração requer autorização de um supervisor.\nDigite o ID do usuário autorizador:\n\n${lista}`
    );
    if (!escolha) return null;
    return Number(escolha);
  } catch {
    alert("Erro ao carregar lista de colaboradores para autorização.");
    return null;
  }
};

const AJUDA = {
  titulo: "Contas a Pagar",
  descricao:
    "Registre aqui toda despesa da fazenda que tem um fornecedor e uma data de vencimento — desde a compra de insumos até serviços prestados. Cada lançamento pode ser dividido em parcelas, e o sistema calcula juros/multa/desconto e o status (pendente, parcial, paga, atrasada) automaticamente.",
  secoes: [
    {
      titulo: "Dados Básicos",
      campos: [
        {
          nome: "Fornecedor",
          obrigatorio: true,
          explicacao:
            "A quem a fazenda deve pagar. Se o fornecedor ainda não existe, use o botão '+' ao lado do campo para cadastrá-lo sem sair desta tela.",
        },
        {
          nome: "Descrição",
          obrigatorio: false,
          explicacao: "Um resumo curto do que é essa despesa (ex.: 'Compra de adubo', 'Manutenção do trator'). Ajuda a identificar o lançamento depois na lista.",
        },
        {
          nome: "Documento",
          obrigatorio: false,
          explicacao: "Número da nota fiscal, contrato ou recibo relacionado a essa despesa.",
        },
        {
          nome: "Tipo de Conta",
          obrigatorio: false,
          explicacao: "'Variável' para despesas que mudam de valor a cada compra; 'Fixa' para despesas recorrentes de valor parecido todo mês.",
        },
        {
          nome: "Categoria Financeira / Centro de Custos",
          obrigatorio: false,
          explicacao: "Classificam a despesa para os relatórios financeiros (ex.: em qual categoria e em qual centro de custo essa despesa deve entrar).",
        },
        {
          nome: "Valor",
          obrigatorio: true,
          explicacao: "Valor total da conta ANTES de juros, multa ou desconto — esses ajustes são aplicados depois, na seção 'Juros, Multa e Desconto'.",
        },
      ],
    },
    {
      titulo: "Datas",
      campos: [
        {
          nome: "Data da Conta",
          obrigatorio: false,
          explicacao: "Data de emissão da nota/contrato de origem da despesa.",
        },
        {
          nome: "Data de Vencimento",
          obrigatorio: true,
          explicacao: "Data limite para o pagamento. Se passar dessa data sem pagamento, o status muda automaticamente para 'Atrasada'.",
        },
      ],
    },
    {
      titulo: "Forma de Pagamento e Parcelas",
      campos: [
        {
          nome: "Forma de Pagamento",
          obrigatorio: false,
          explicacao:
            "Ao escolher uma forma de pagamento já configurada, o sistema preenche automaticamente juros, multa e número de parcelas com os valores padrão dela — você ainda pode ajustar manualmente depois.",
        },
        {
          nome: "Parcelamento (nº / total)",
          obrigatorio: false,
          explicacao: "Em quantas parcelas essa conta será dividida. Clique em 'Ver Parcelas' para gerar e revisar cada data e valor individualmente antes de salvar.",
        },
      ],
    },
    {
      titulo: "Juros, Multa e Desconto",
      campos: [
        {
          nome: "Juros / Multa / Desconto",
          obrigatorio: false,
          explicacao:
            "Podem ser lançados em R$ (valor fixo) ou em % sobre o valor total — escolha o botão correspondente ao lado de cada campo. O 'Valor Final da Conta', exibido no fim do formulário, já mostra o resultado com esses ajustes aplicados.",
        },
      ],
    },
  ],
  dicas: [
    "Ao criar uma conta com várias parcelas, cada parcela é salva como um lançamento próprio, ligado às demais pelo mesmo grupo — por isso, ao excluir, o sistema pergunta se é para excluir só aquela parcela ou o grupo inteiro.",
    "Registrar um pagamento, estornar um pagamento e excluir um grupo de parcelas são ações que exigem conexão com a internet, mesmo com o sistema funcionando offline para o resto — elas envolvem conferência direto no servidor.",
    "O ícone de moeda na coluna Ações abre a tela de registrar pagamento; o ícone de seta circular estorna um pagamento já feito.",
  ],
};

export default function GestaoContasAPagar() {
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mensagem, setMensagem] = useState("");
  const [parcelasPreview, setParcelasPreview] = useState([]);
  const [mostrarModalPagamento, setMostrarModalPagamento] = useState(false);
  const [contaSelecionada, setContaSelecionada] = useState(null);
  
  // Dados para os selects
  const [fornecedores, setFornecedores] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [notas, setNotas] = useState([]);
  const [centros, setCentros] = useState([]);
  const [formasPagamento, setFormasPagamento] = useState([]);
  const [categoriasMestras, setCategoriasMestras] = useState([]);

  // Cadastro rápido via modal (Fornecedor, Categoria, Centro de Custo, Forma de Pagamento)
  const [mostrarModalFornecedor, setMostrarModalFornecedor] = useState(false);
  const [mostrarModalCategoria, setMostrarModalCategoria] = useState(false);
  const [mostrarModalCentroCusto, setMostrarModalCentroCusto] = useState(false);
  const [mostrarModalFormaPagamento, setMostrarModalFormaPagamento] = useState(false);

  // Filtros
  const [filtros, setFiltros] = useState({
    fornecedor: "",
    busca: "",
    data_inicio: "",
    data_fim: "",
    status: "todos",
    valor_min: "",
    valor_max: ""
  });
  
  const [resultados, setResultados] = useState([]);
  const [resultadosFiltrados, setResultadosFiltrados] = useState([]);
  
  // Paginação
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina] = useState(10);
  
  const [conta, setConta] = useState({
    descricao: "",
    documento: "",
    valor_total: "",
    valor_pago: "",
    juros: "0",
    multa: "0",
    desconto: "0",
    data_conta: "",
    data_vencimento: "",
    fornecedor: "",
    categoria: "",
    centro_custo: "",
    nota_fiscal: "",
    numero_parcela: "1",
    total_parcelas: "1",
    tipo: "variavel",
    observacoes: "",
    juros_tipo: "valor",
    multa_tipo: "valor",
    desconto_tipo: "valor",
    forma_pagamento: "",
    data_pagamento: null
  });

  const [pagamento, setPagamento] = useState({
    valor: "",
    data: new Date().toISOString().split("T")[0],
    forma_pagamento: "",
    observacao: "",
  });

  // Status options com cores
  const statusOptions = [
    { value: "todos", label: "Todas", cor: "bg-gray-100 text-gray-800" },
    { value: "pendente", label: "Pendente", cor: "bg-yellow-100 text-yellow-800"},
    { value: "parcial", label: "Parcial", cor: "bg-blue-100 text-blue-800"},
    { value: "paga", label: "Paga", cor: "bg-green-100 text-green-800"},
    { value: "atrasada", label: "Atrasada", cor: "bg-red-100 text-red-800"},
  ];

  // Tipos de conta
  const tiposConta = [
    { value: "variavel", label: "Variável", descricao: "Valor pode variar mês a mês" },
    { value: "fixa", label: "Fixa", descricao: "Valor fixo recorrente" }
  ];

  // Carregar dados iniciais
  const carregarDados = useCallback(async () => {
    try {
      const [fp, f, c, n, cc, cm] = await Promise.all([
        repositorioFormaPagamento.listar(),
        repositorioFornecedor.listar(),
        repositorioCategoriaFinanceira.listar(),
        repositorioNotaFiscal.listar(),
        repositorioCentroCusto.listar(),
        repositorioCategoriaMestra.listar(),
      ]);

      setFormasPagamento(Array.isArray(fp.data) ? fp.data : (fp.data.results || []));
      setFornecedores(Array.isArray(f.data) ? f.data : (f.data.results || []));
      setCategorias(Array.isArray(c.data) ? c.data : (c.data.results || []));
      setNotas(Array.isArray(n.data) ? n.data : (n.data.results || []));
      setCentros(Array.isArray(cc.data) ? cc.data : (cc.data.results || []));
      setCategoriasMestras(Array.isArray(cm.data) ? cm.data : (cm.data.results || []));
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
    }
  }, []);

  // Buscar contas
  const buscarContas = useCallback(async () => {
    try {
      setCarregandoLista(true);
      const params = new URLSearchParams();
      
      if (filtros.fornecedor) params.append("fornecedor", filtros.fornecedor);
      if (filtros.status !== "todos") params.append("status", filtros.status);
      if (filtros.data_inicio) params.append("data_inicio", filtros.data_inicio);
      if (filtros.data_fim) params.append("data_fim", filtros.data_fim);
      if (filtros.busca) params.append("termo", filtros.busca);
      if (filtros.valor_min) params.append("valor_min", filtros.valor_min);
      if (filtros.valor_max) params.append("valor_max", filtros.valor_max);
      params.append("ordering", "-data_vencimento");
      
      const response = await repositorioContaPagar.listar(`?${params}`);
      const dados = response.data.results || response.data;
      setResultados(Array.isArray(dados) ? dados : []);
      setResultadosFiltrados(Array.isArray(dados) ? dados : []);
      setPaginaAtual(1);
    } catch (error) {
      console.error("Erro ao buscar contas:", error);
      alert("Erro ao carregar lista de contas");
    } finally {
      setCarregandoLista(false);
    }
  }, [filtros]);

  useEffect(() => {
    carregarDados();
    buscarContas();
  }, [carregarDados, buscarContas]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setConta(prev => ({ ...prev, [name]: value }));
  };

  const selecionarForma = (id) => {
    const forma = formasPagamento.find((f) => f.id == id);
    setConta(prev => ({
      ...prev,
      forma_pagamento: id,
      juros: forma?.juros_atraso || "0",
      multa: forma?.multa_atraso || "0",
      total_parcelas: forma?.permite_parcelamento ? forma?.max_parcelas || "1" : "1",
      numero_parcela: "1",
      data_vencimento: prev.data_conta && forma?.prazo_dias
        ? new Date(new Date(prev.data_conta).setDate(new Date(prev.data_conta).getDate() + forma.prazo_dias)).toISOString().split("T")[0]
        : prev.data_vencimento,
    }));
  };

  const calcularValorFinal = () => {
    const total = Number(conta.valor_total) || 0;
    const juros = conta.juros_tipo === "porcentagem" ? total * (Number(conta.juros) / 100) : Number(conta.juros);
    const multa = conta.multa_tipo === "porcentagem" ? total * (Number(conta.multa) / 100) : Number(conta.multa);
    const desconto = conta.desconto_tipo === "porcentagem" ? total * (Number(conta.desconto) / 100) : Number(conta.desconto);
    return total + juros + multa - desconto;
  };

  const gerarPreviewParcelas = () => {
    if (!conta.data_conta && !conta.data_vencimento) {
      alert("Informe a data da conta ou data de vencimento primeiro.");
      return;
    }
    
    const forma = formasPagamento.find(f => f.id == conta.forma_pagamento);
    const totalParcelas = Number(conta.total_parcelas) || 1;
    // valor_total guarda o valor ANTES de juros/multa/desconto — esses
    // ajustes são aplicados pelo backend ao calcular valor_final, então
    // dividir aqui o valor JÁ com desconto faria o desconto ser aplicado
    // duas vezes (uma na divisão, outra no cálculo do backend).
    const valorBase = (Number(conta.valor_total) || 0) / totalParcelas;
    const intervalo = forma?.intervalo_parcelas || 30;
    const prazoInicial = forma?.prazo_dias || 0;
    const lista = [];

    for (let i = 1; i <= totalParcelas; i++) {
      const dataBaseInicial = conta.data_conta || conta.data_vencimento;
      if (!dataBaseInicial) return;
      const dataBase = new Date(dataBaseInicial);
      if (isNaN(dataBase)) return;
      const dias = prazoInicial + (i - 1) * intervalo;
      dataBase.setDate(dataBase.getDate() + dias);
      lista.push({
        numero: i,
        total: totalParcelas,
        valor: Number(valorBase.toFixed(2)),
        data_vencimento: dataBase.toISOString().split("T")[0],
      });
    }
    setParcelasPreview(lista);
  };

  const validarFormulario = () => {
    if (!conta.fornecedor) {
      alert("Selecione um fornecedor.");
      return false;
    }
    if (!conta.data_vencimento) {
      alert("Informe a data de vencimento.");
      return false;
    }
    if (!conta.valor_total || Number(conta.valor_total) <= 0) {
      alert("Informe um valor total válido.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validarFormulario()) return;
    
    setLoading(true);
    const hoje = new Date().toISOString().split("T")[0];
    const valorFinal = calcularValorFinal();
    const valorPago = Number(conta.valor_pago) || 0;

    let status = "pendente";
    if (valorPago === 0) status = "pendente";
    else if (valorPago < valorFinal) status = "parcial";
    else status = "paga";
    if (status === "pendente" && conta.data_vencimento < hoje) status = "atrasada";

    try {
      if (editando) {
        const payload = {
          ...conta,
          status,
          juros: Number(conta.juros) || 0,
          multa: Number(conta.multa) || 0,
          desconto: Number(conta.desconto) || 0,
        };
        try {
          await repositorioContaPagar.atualizar(editandoId, payload);
        } catch (error) {
          if (error.response?.data?.autorizacao_necessaria) {
            const autorizadoPor = await solicitarAutorizacao();
            if (!autorizadoPor) return;
            await repositorioContaPagar.atualizar(editandoId, { ...payload, autorizado_por: autorizadoPor });
          } else {
            throw error;
          }
        }
        setMensagem("✅ Conta atualizada com sucesso!");
        handleCancel();
      } else {
        // valor_total enviado à API é o valor ANTES de juros/multa/desconto
        // — o backend aplica esses ajustes ao calcular valor_final. Usar
        // calcularValorFinal() aqui geraria desconto em dobro (uma vez na
        // gravação, outra no cálculo do backend).
        const valorBase = Number(conta.valor_total) || 0;
        const lista = parcelasPreview.length > 0 ? parcelasPreview : [{
          numero: 1, total: 1, valor: valorBase, data_vencimento: conta.data_vencimento
        }];
        
        const grupo = Date.now().toString();
        let idContaPai = null;
        
        for (let i = 0; i < lista.length; i++) {
          const p = lista[i];
          const novaConta = { 
            ...conta, 
            valor_pago: valorPago, 
            status, 
            grupo_parcela: grupo, 
            numero_parcela: Number(p.numero), 
            total_parcelas: Number(p.total), 
            valor_total: Number(p.valor), 
            data_vencimento: p.data_vencimento, 
            conta_pai: idContaPai 
          };
          const response = await repositorioContaPagar.criar(novaConta);
          if (i === 0) idContaPai = response.data.id;
        }
        setMensagem("✅ Conta cadastrada com sucesso!");
        handleCancel();
      }
      
      buscarContas();
      setTimeout(() => setMensagem(""), 3000);
    } catch (error) {
      console.error("Erro:", error);
      alert(`❌ ${extrairMensagemErro(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (item) => {
    setConta({ ...item });
    setEditando(true);
    setEditandoId(item.id);
    setMostrarFormulario(true);
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };

  const handleNovo = () => {
    handleCancel();
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setConta({
      descricao: "", documento: "", valor_total: "", valor_pago: "", juros: "0", multa: "0", desconto: "0",
      data_conta: "", data_vencimento: "", fornecedor: "", categoria: "", centro_custo: "", nota_fiscal: "",
      numero_parcela: "1", total_parcelas: "1", tipo: "variavel", observacoes: "", juros_tipo: "valor",
      multa_tipo: "valor", desconto_tipo: "valor", forma_pagamento: "", data_pagamento: null
    });
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(false);
    setParcelasPreview([]);
    setMensagem("");
  };

  const handleExcluir = async (item) => {
    const escolha = window.prompt(
      "📌 Opções de exclusão:\n\n" +
      "1 - Excluir apenas esta parcela\n" +
      "2 - Excluir TODAS as parcelas deste grupo\n\n" +
      "Digite o número da opção desejada:"
    );
    if (!escolha) return;
    
    try {
      if (escolha === "1") {
        await repositorioContaPagar.excluir(item.id);
        alert("✅ Parcela excluída com sucesso!");
      } else if (escolha === "2") {
        await api.post("/financeiro/contas-a-pagar/excluir-grupo/", { grupo_parcela: item.grupo_parcela });
        alert("✅ Grupo de parcelas excluído com sucesso!");
      } else {
        alert("Opção inválida");
        return;
      }
      buscarContas();
    } catch (error) {
      console.error("Erro ao excluir:", error);
      alert(`❌ ${extrairMensagemErro(error)}`);
    }
  };

  const estornarPagamento = async (item) => {
    if (!window.confirm("⚠️ Deseja estornar o pagamento desta conta?")) return;
    const justificativa = window.prompt("Justificativa do estorno (deixe em branco se não for exigida):") || "";
    try {
      await api.post(`/financeiro/contas-a-pagar/${item.id}/estornar/`, { justificativa });
      alert("✅ Pagamento estornado com sucesso!");
      buscarContas();
    } catch (error) {
      console.error("Erro ao estornar:", error);
      alert(`❌ ${extrairMensagemErro(error)}`);
    }
  };

  const confirmarPagamento = async () => {
    if (!pagamento.valor || !pagamento.data) {
      alert("Preencha o valor e a data do pagamento.");
      return;
    }
    
    const valor = Number(pagamento.valor || 0);
    const restante = Number(contaSelecionada?.valor_restante || 0);
    
    if (valor <= 0) {
      alert("Valor inválido.");
      return;
    }
    
    if (valor > restante) {
      alert(`Valor maior que o restante (R$ ${restante.toFixed(2)})`);
      return;
    }
    
    try {
      await api.post(`/financeiro/contas-a-pagar/${contaSelecionada.id}/pagar/`, {
        valor, 
        data: pagamento.data, 
        forma_pagamento: pagamento.forma_pagamento, 
        observacao: pagamento.observacao
      });
      setMostrarModalPagamento(false);
      setContaSelecionada(null);
      setPagamento({ valor: "", data: new Date().toISOString().split("T")[0], forma_pagamento: "", observacao: "" });
      buscarContas();
      alert("✅ Pagamento registrado com sucesso!");
    } catch (error) {
      console.error("Erro ao pagar:", error);
      alert(`❌ ${extrairMensagemErro(error)}`);
    }
  };

  const aplicarFiltros = () => {
    let filtrados = [...resultados];
    if (filtros.fornecedor) filtrados = filtrados.filter(c => c.fornecedor == filtros.fornecedor);
    if (filtros.status !== "todos") filtrados = filtrados.filter(c => c.status === filtros.status);
    if (filtros.data_inicio) filtrados = filtrados.filter(c => c.data_vencimento >= filtros.data_inicio);
    if (filtros.data_fim) filtrados = filtrados.filter(c => c.data_vencimento <= filtros.data_fim);
    if (filtros.valor_min) filtrados = filtrados.filter(c => (c.valor_final || c.valor_total) >= Number(filtros.valor_min));
    if (filtros.valor_max) filtrados = filtrados.filter(c => (c.valor_final || c.valor_total) <= Number(filtros.valor_max));
    if (filtros.busca) {
      const buscaLower = filtros.busca.toLowerCase();
      filtrados = filtrados.filter(c => 
        c.descricao?.toLowerCase().includes(buscaLower) || 
        c.documento?.toLowerCase().includes(buscaLower) ||
        c.fornecedor_nome?.toLowerCase().includes(buscaLower)
      );
    }
    setResultadosFiltrados(filtrados);
    setPaginaAtual(1);
  };

  const limparFiltros = () => {
    setFiltros({ fornecedor: "", busca: "", data_inicio: "", data_fim: "", status: "todos", valor_min: "", valor_max: "" });
    setResultadosFiltrados(resultados);
    setPaginaAtual(1);
  };

  const formatarData = (data) => data ? new Date(data).toLocaleDateString("pt-BR", { timeZone: "UTC" }) : "—";
  const formatarMoeda = (valor) => `R$ ${Number(valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
  const getStatusInfo = (status) => statusOptions.find(s => s.value === status) || statusOptions[0];
  
  const totalFiltrado = resultadosFiltrados.reduce((acc, item) => acc + Number(item.valor_final || item.valor_total), 0);
  const indiceUltimoItem = paginaAtual * itensPorPagina;
  const indicePrimeiroItem = indiceUltimoItem - itensPorPagina;
  const resultadosPaginados = resultadosFiltrados.slice(indicePrimeiroItem, indiceUltimoItem);
  const totalPaginas = Math.ceil(resultadosFiltrados.length / itensPorPagina);

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">Gestão de Contas a Pagar <MdPayments size={38} /></h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovo}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nova Conta a Pagar
            </button>
          )}
        </div>

        {mensagem && (
          <div className="mb-4 p-3 rounded-lg bg-green-100 text-green-700 border border-green-200">
            {mensagem}
          </div>
        )}

        {/* Formulário de Cadastro/Edição */}
        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {editando ? "Editar Conta a Pagar" : "Nova Conta a Pagar"}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar formulário"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {/* Seção 1: Dados Básicos */}
                <div className="border-b pb-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Fornecedor *</label>
                      <div className="flex items-center gap-2">
                        <select
                          name="fornecedor"
                          value={conta.fornecedor}
                          onChange={handleChange}
                          className="w-36 flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                          required
                        >
                          <option value="">Selecione um fornecedor...</option>
                          {fornecedores.map(f => (
                            <option key={f.id} value={f.id}>{nomePessoa(f) || "—"}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setMostrarModalFornecedor(true)}
                          title="Cadastrar novo fornecedor"
                          className="p-2 hover:bg-gray-200 rounded"
                        >
                          <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                        </button>
                      </div>
                      <span className="text-xs text-gray-500 mt-1">Empresa ou pessoa a quem devemos pagar</span>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Descrição</label>
                      <input
                        type="text"
                        name="descricao"
                        value={conta.descricao}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        placeholder="Ex: Compra de insumos, Serviço de manutenção..."
                      />
                      <span className="text-xs text-gray-500 mt-1">Identificação da despesa</span>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Documento</label>
                      <input
                        type="text"
                        name="documento"
                        value={conta.documento}
                        onChange={handleChange}
                        className="w-64 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        placeholder="Nº da nota fiscal, contrato..."
                      />
                      <span className="text-xs text-gray-500 mt-1">Número do documento fiscal</span>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Tipo de Conta</label>
                      <select
                        name="tipo"
                        value={conta.tipo}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      >
                        {tiposConta.map(t => (
                          <option key={t.value} value={t.value}>{t.icon} {t.label}</option>
                        ))}
                      </select>
                      <span className="text-xs text-gray-500 mt-1">{tiposConta.find(t => t.value === conta.tipo)?.descricao}</span>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Categoria Financeira</label>
                      <div className="flex items-center gap-2">
                        <select
                          name="categoria"
                          value={conta.categoria}
                          onChange={handleChange}
                          className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        >
                          <option value="">Selecione uma categoria...</option>
                          {categorias.map(c => (
                            <option key={c.id} value={c.id}>{c.nome}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setMostrarModalCategoria(true)}
                          title="Cadastrar nova categoria"
                          className="p-2 hover:bg-gray-200 rounded"
                        >
                          <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Centro de Custos</label>
                      <div className="flex items-center gap-2">
                        <select
                          name="centro_custo"
                          value={conta.centro_custo}
                          onChange={handleChange}
                          className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        >
                          <option value="">Selecione...</option>
                          {centros.map(c => (
                            <option key={c.id} value={c.id}>{c.nome}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setMostrarModalCentroCusto(true)}
                          title="Cadastrar novo centro de custos"
                          className="p-2 hover:bg-gray-200 rounded"
                        >
                          <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-col w-48">
                      <label className="font-medium">Valor</label>
                      <div className="flex items-center border border-green-500 rounded">
                        <input type="number" placeholder="0,00"
                        value={conta.valor_total}
                        onChange={(e) => setConta({ ...conta, valor_total: e.target.value })}
                        className="w-full p-2 outline-none rounded-l"
                        />

                        <div className="h-6 w-px bg-green-500" />
                        <span className="px-3 text-gray-700 bg-gray-100 rounded-r">
                          R$
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Seção 2: Datas */}
                <div className="border-b pb-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Data da Conta</label>
                      <input
                        type="date"
                        name="data_conta"
                        value={conta.data_conta}
                        onChange={handleChange}
                        className="w-36 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      />
                      <span className="text-xs text-gray-500 mt-1">Data de emissão da nota/contrato</span>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Data de Vencimento *</label>
                      <input
                        type="date"
                        name="data_vencimento"
                        value={conta.data_vencimento}
                        onChange={handleChange}
                        className="w-36 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        required
                      />
                      <span className="text-xs text-red-500 mt-1">Data limite para pagamento</span>
                    </div>
                  </div>
                </div>

                {/* Seção 3: Forma de Pagamento e Parcelas */}
                <div className="border-b pb-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Forma de Pagamento</label>
                      <div className="flex items-center gap-2">
                        <select
                          value={conta.forma_pagamento || ""}
                          onChange={(e) => selecionarForma(e.target.value)}
                          className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        >
                          <option value="">Selecione...</option>
                          {formasPagamento.map(f => (
                            <option key={f.id} value={f.id}>{f.nome}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setMostrarModalFormaPagamento(true)}
                          title="Cadastrar nova forma de pagamento"
                          className="p-2 hover:bg-gray-200 rounded"
                        >
                          <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Parcelamento</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          name="numero_parcela"
                          value={conta.numero_parcela}
                          onChange={handleChange}
                          className="w-20 border border-green-500 p-2 rounded text-center"
                          min="1"
                        />
                        <span className="text-gray-500">/</span>
                        <input
                          type="number"
                          name="total_parcelas"
                          value={conta.total_parcelas}
                          onChange={handleChange}
                          className="w-20 border border-green-500 p-2 rounded text-center"
                          min="1"
                        />
                      </div>
                    </div>

                    <div className="flex flex-col justify-end">
                      <button
                        type="button"
                        onClick={gerarPreviewParcelas}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded transition-colors"
                      >
                        Ver Parcelas
                      </button>
                    </div>
                  </div>

                  {/* Preview das Parcelas */}
                  {parcelasPreview.length > 0 && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <h5 className="font-semibold text-gray-700 mb-3">Preview das Parcelas</h5>
                      <div className="space-y-2">
                        {parcelasPreview.map((p, i) => (
                          <div key={i} className="flex flex-wrap gap-4 items-center p-2 bg-white rounded border">
                            <span className="font-medium w-16">Parcela {p.numero}/{p.total}</span>
                            <input
                              type="date"
                              value={p.data_vencimento}
                              onChange={(e) => {
                                const novas = [...parcelasPreview];
                                novas[i].data_vencimento = e.target.value;
                                setParcelasPreview(novas);
                              }}
                              className="border border-green-500 p-1 rounded"
                            />
                            <div className="flex items-center border border-green-500 rounded">
                              <span className="px-2 text-gray-700 bg-gray-100 rounded-l">R$</span>
                              <input
                                type="number"
                                step="0.01"
                                value={p.valor}
                                onChange={(e) => {
                                  const novas = [...parcelasPreview];
                                  novas[i].valor = Number(e.target.value);
                                  setParcelasPreview(novas);
                                }}
                                className="w-28 p-1 rounded-r"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Seção 4: Juros, Multa e Desconto */}
                <div className="border-b pb-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* JUROS */}
                    <div className="flex flex-col w-36">
                      <label className="text-sm font-medium text-gray-700 mb-1">Juros</label>
                      <div className="flex items-center border border-green-500 rounded">
                        <input
                          type="number"
                          name="juros"
                          value={conta.juros}
                          onChange={handleChange}
                          className="w-24 p-2 rounded-l focus:ring-2 focus:ring-green-300"
                          step="0.01"
                          min="0"
                          placeholder="0,00"
                        />
                        <div className="h-6 w-px bg-green-500" />
                        <span className="px-3 text-gray-700 bg-gray-100 rounded-r">
                          {conta.juros_tipo === "porcentagem" ? "%" : "R$"}
                        </span>
                      </div>
                      <div className="flex gap-4 mt-2">
                        <label className="flex items-center gap-1 text-sm">
                          <input type="radio" name="juros_tipo" value="valor" checked={conta.juros_tipo === "valor"} onChange={handleChange} /> R$
                        </label>
                        <label className="flex items-center gap-1 text-sm">
                          <input type="radio" name="juros_tipo" value="porcentagem" checked={conta.juros_tipo === "porcentagem"} onChange={handleChange} /> %
                        </label>
                      </div>
                      <span className="text-xs text-gray-500 mt-1">Aplicado em caso de atraso</span>
                    </div>

                    {/* MULTA */}
                    <div className="flex flex-col w-36">
                      <label className="text-sm font-medium text-gray-700 mb-1">Multa</label>
                      <div className="flex items-center border border-green-500 rounded">
                        <input
                          type="number"
                          name="multa"
                          value={conta.multa}
                          onChange={handleChange}
                          className="w-24 p-2 rounded-l focus:ring-2 focus:ring-green-300"
                          step="0.01"
                          min="0"
                          placeholder="0,00"
                        />
                        <div className="h-6 w-px bg-green-500" />
                        <span className="px-3 text-gray-700 bg-gray-100 rounded-r">
                          {conta.multa_tipo === "porcentagem" ? "%" : "R$"}
                        </span>
                      </div>
                      <div className="flex gap-4 mt-2">
                        <label className="flex items-center gap-1 text-sm">
                          <input type="radio" name="multa_tipo" value="valor" checked={conta.multa_tipo === "valor"} onChange={handleChange} /> R$
                        </label>
                        <label className="flex items-center gap-1 text-sm">
                          <input type="radio" name="multa_tipo" value="porcentagem" checked={conta.multa_tipo === "porcentagem"} onChange={handleChange} /> %
                        </label>
                      </div>
                    </div>

                    {/* DESCONTO */}
                    <div className="flex flex-col w-36">
                      <label className="text-sm font-medium text-gray-700 mb-1">Desconto</label>
                      <div className="flex items-center border border-green-500 rounded">
                        <input
                          type="number"
                          name="desconto"
                          value={conta.desconto}
                          onChange={handleChange}
                          className="w-24 p-2 rounded-l focus:ring-2 focus:ring-green-300"
                          step="0.01"
                          min="0"
                          placeholder="0,00"
                        />
                        <div className="h-6 w-px bg-green-500" />
                        <span className="px-3 text-gray-700 bg-gray-100 rounded-r">
                          {conta.desconto_tipo === "porcentagem" ? "%" : "R$"}
                        </span>
                      </div>
                      <div className="flex gap-4 mt-2">
                        <label className="flex items-center gap-1 text-sm">
                          <input type="radio" name="desconto_tipo" value="valor" checked={conta.desconto_tipo === "valor"} onChange={handleChange} /> R$
                        </label>
                        <label className="flex items-center gap-1 text-sm">
                          <input type="radio" name="desconto_tipo" value="porcentagem" checked={conta.desconto_tipo === "porcentagem"} onChange={handleChange} /> %
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Seção 5: Observações */}
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Observações</label>
                  <textarea
                    name="observacoes"
                    value={conta.observacoes}
                    onChange={handleChange}
                    rows={3}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    placeholder="Informações adicionais sobre esta conta..."
                  />
                </div>

                {/* Valor Final */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-700">Valor Final da Conta:</span>
                    <span className="text-2xl font-bold text-green-600">{formatarMoeda(calcularValorFinal())}</span>
                  </div>
                </div>

                {/* Botões */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-blue-300 flex items-center gap-2"
                  >
                    {loading ? "💾 Salvando..." : (editando ? "Atualizar" : "Cadastrar")}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* Filtros e Tabela - visíveis apenas quando formulário está fechado */}
        {!mostrarFormulario && (
          <>
            {/* Seção de Filtros */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filtros de Busca
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Fornecedor</label>
                  <select
                    value={filtros.fornecedor}
                    onChange={(e) => setFiltros({...filtros, fornecedor: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  >
                    <option value="">Todos os fornecedores</option>
                    {fornecedores.map(f => (
                      <option key={f.id} value={f.id}>{nomePessoa(f) || "—"}</option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Buscar por termo</label>
                  <input
                    type="text"
                    placeholder="Descrição, documento ou fornecedor..."
                    value={filtros.busca}
                    onChange={(e) => setFiltros({...filtros, busca: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Data Início</label>
                  <input
                    type="date"
                    value={filtros.data_inicio}
                    onChange={(e) => setFiltros({...filtros, data_inicio: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Data Fim</label>
                  <input
                    type="date"
                    value={filtros.data_fim}
                    onChange={(e) => setFiltros({...filtros, data_fim: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Valor Mínimo</label>
                  <div className="flex items-center border border-green-500 rounded">
                    <span className="px-2 text-gray-700 bg-gray-100 rounded-l">R$</span>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0,00"
                      value={filtros.valor_min}
                      onChange={(e) => setFiltros({...filtros, valor_min: e.target.value})}
                      className="flex-1 p-2 rounded-r focus:ring-2 focus:ring-green-300"
                    />
                  </div>
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Valor Máximo</label>
                  <div className="flex items-center border border-green-500 rounded">
                    <span className="px-2 text-gray-700 bg-gray-100 rounded-l">R$</span>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0,00"
                      value={filtros.valor_max}
                      onChange={(e) => setFiltros({...filtros, valor_max: e.target.value})}
                      className="flex-1 p-2 rounded-r focus:ring-2 focus:ring-green-300"
                    />
                  </div>
                </div>
              </div>

              {/* Filtros rápidos por status */}
              <div className="flex flex-wrap gap-2 mt-4">
                {statusOptions.map(s => (
                  <button
                    key={s.value}
                    onClick={() => setFiltros({...filtros, status: s.value})}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      filtros.status === s.value ? s.cor : "bg-gray-200 text-gray-700"
                    }`}
                  >
                    {s.icon && <span className="mr-1">{s.icon}</span>}
                    {s.label}
                  </button>
                ))}
              </div>

              <div className="flex gap-3 mt-4">
                <button
                  onClick={aplicarFiltros}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors flex items-center gap-2"
                >
                  Aplicar Filtros
                </button>
                <button
                  onClick={limparFiltros}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                >
                  Limpar Filtros
                </button>
              </div>

              <div className="mt-4 text-right">
                <span className="text-lg font-semibold text-gray-700">Total filtrado: </span>
                <span className="text-xl font-bold text-green-600">{formatarMoeda(totalFiltrado)}</span>
              </div>
            </div>

            {/* Tabela de Contas */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center flex-wrap gap-2">
                <p className="text-gray-600">
                  {resultadosFiltrados.length} conta(s) encontrada(s)
                </p>
                <div className="text-sm text-gray-500">
                  Página {paginaAtual} de {totalPaginas || 1}
                </div>
              </div>
              
              {carregandoLista ? (
                <div className="p-8 text-center text-gray-500">Carregando...</div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">ID</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Fornecedor</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Descrição</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Vencimento</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Valor</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Restante</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Parcela</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Status</th>
                          <th className="p-3 text-center text-sm font-semibold text-gray-700">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {resultadosPaginados.map(item => {
                          const statusInfo = getStatusInfo(item.status);
                          return (
                            <tr key={item.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                              <td className="p-3 text-sm font-mono">{item.id}</td>
                              <td className="p-3 text-sm">{item.fornecedor_nome || "-"}</td>
                              <td className="p-3 text-sm">{item.descricao || "-"}</td>
                              <td className="p-3 text-sm">{formatarData(item.data_vencimento)}</td>
                              <td className="p-3 text-sm font-medium">{formatarMoeda(item.valor_total_conta ?? item.valor_final ?? item.valor_total)}</td>
                              <td className="p-3 text-sm">{formatarMoeda(item.valor_restante)}</td>
                              <td className="p-3 text-sm text-center">{item.numero_parcela}/{item.total_parcelas}</td>
                              <td className="p-3">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.cor}`}>
                                  {statusInfo.icon} {statusInfo.label}
                                </span>
                              </td>
                              <td className="p-3">
                                <div className="flex justify-center gap-2">
                                  {item.valor_pago > 0 && (
                                    <button
                                      onClick={() => estornarPagamento(item)}
                                      className="text-gray-800 p-1 rounded hover:bg-yellow-100 transition-colors"
                                      title="Estornar pagamento"
                                    >
                                      <FaArrowRotateLeft size={23}/>
                                    </button>
                                  )}
                                  {item.status !== "paga" && (
                                    <button
                                      onClick={() => {
                                        setContaSelecionada(item);
                                        setPagamento({
                                          valor: item.valor_restante,
                                          data: new Date().toISOString().split("T")[0],
                                          forma_pagamento: "",
                                          observacao: ""
                                        });
                                        setMostrarModalPagamento(true);
                                      }}
                                      className="text-gray-800 p-1 rounded transition-colors hover:bg-green-100"
                                      title="Registrar pagamento"
                                    >
                                      <TbCoinFilled size={26} />
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleEditar(item)}
                                    className="text-gray-800 p-1 rounded hover:bg-yellow-100 transition-colors"
                                    title="Editar conta"
                                  >
                                    <HiPencilAlt size={26}/>
                                  </button>
                                  <button
                                    onClick={() => handleExcluir(item)}
                                    className="text-gray-800 p-1 rounded hover:bg-red-100 transition-colors"
                                    title="Excluir conta"
                                  >
                                    <RiDeleteBin5Fill size={26}/>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        
                        {resultadosPaginados.length === 0 && (
                          <tr>
                            <td colSpan="9" className="p-8 text-center text-gray-500">
                              {filtros.fornecedor || filtros.busca || filtros.status !== "todos" || filtros.data_inicio || filtros.data_fim
                                ? "Nenhuma conta encontrada com os filtros selecionados."
                                : "Nenhuma conta a pagar cadastrada."}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Paginação */}
                  {totalPaginas > 1 && (
                    <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-center items-center gap-2">
                      <button
                        onClick={() => setPaginaAtual(prev => Math.max(prev - 1, 1))}
                        disabled={paginaAtual === 1}
                        className={`px-3 py-1 rounded transition-colors ${paginaAtual === 1 ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"}`}
                      >
                        ◀ Anterior
                      </button>
                      <div className="flex gap-1">
                        {Array.from({ length: Math.min(totalPaginas, 5) }, (_, i) => {
                          let num;
                          if (totalPaginas <= 5) num = i + 1;
                          else if (paginaAtual <= 3) num = i + 1;
                          else if (paginaAtual >= totalPaginas - 2) num = totalPaginas - 4 + i;
                          else num = paginaAtual - 2 + i;
                          return (
                            <button
                              key={num}
                              onClick={() => setPaginaAtual(num)}
                              className={`w-8 h-8 rounded transition-colors ${paginaAtual === num ? "bg-green-600 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}
                            >
                              {num}
                            </button>
                          );
                        })}
                      </div>
                      <button
                        onClick={() => setPaginaAtual(prev => Math.min(prev + 1, totalPaginas))}
                        disabled={paginaAtual === totalPaginas}
                        className={`px-3 py-1 rounded transition-colors ${paginaAtual === totalPaginas ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"}`}
                      >
                        Próxima ▶
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {/* Modal de Pagamento */}
        {mostrarModalPagamento && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-[500px] max-w-[90%] p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-800">Registrar Pagamento</h3>
                <button
                  onClick={() => setMostrarModalPagamento(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valor Restante</label>
                  <div className="text-2xl font-bold text-green-600">
                    {formatarMoeda(contaSelecionada?.valor_restante)}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valor a Pagar *</label>
                  <div className="flex items-center border border-green-500 rounded">
                    <span className="px-3 text-gray-700 bg-gray-100 rounded-l">R$</span>
                    <input
                      type="number"
                      step="0.01"
                      value={pagamento.valor}
                      onChange={(e) => setPagamento({...pagamento, valor: e.target.value})}
                      className="flex-1 p-2 rounded-r focus:ring-2 focus:ring-green-300"
                      placeholder="0,00"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data do Pagamento *</label>
                  <input
                    type="date"
                    value={pagamento.data}
                    onChange={(e) => setPagamento({...pagamento, data: e.target.value})}
                    className="w-full border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Forma de Pagamento *</label>
                  <select
                    value={pagamento.forma_pagamento}
                    onChange={(e) => setPagamento({...pagamento, forma_pagamento: e.target.value})}
                    className="w-full border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    required
                  >
                    <option value="">Selecione...</option>
                    {formasPagamento.map(f => (
                      <option key={f.id} value={f.id}>{f.nome}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Observação</label>
                  <textarea
                    value={pagamento.observacao}
                    onChange={(e) => setPagamento({...pagamento, observacao: e.target.value})}
                    className="w-full border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    rows={2}
                    placeholder="Observações sobre este pagamento..."
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setMostrarModalPagamento(false)}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmarPagamento}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
                >
                  Confirmar Pagamento
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <ModalCadastroFornecedor
        isOpen={mostrarModalFornecedor}
        onClose={() => setMostrarModalFornecedor(false)}
        onSuccess={async (novo) => {
          await carregarDados();
          setConta((prev) => ({ ...prev, fornecedor: novo.id }));
        }}
      />
      <ModalCadastroCategoriaFinanceira
        isOpen={mostrarModalCategoria}
        onClose={() => setMostrarModalCategoria(false)}
        categoriasMestras={categoriasMestras}
        onSuccess={async (nova) => {
          await carregarDados();
          setConta((prev) => ({ ...prev, categoria: nova.id }));
        }}
      />
      <ModalCadastroCentroCusto
        isOpen={mostrarModalCentroCusto}
        onClose={() => setMostrarModalCentroCusto(false)}
        onSuccess={async (novo) => {
          await carregarDados();
          setConta((prev) => ({ ...prev, centro_custo: novo.id }));
        }}
      />
      <ModalCadastroFormaPagamento
        isOpen={mostrarModalFormaPagamento}
        onClose={() => setMostrarModalFormaPagamento(false)}
        onSuccess={async (nova) => {
          await carregarDados();
          setConta((prev) => ({ ...prev, forma_pagamento: nova.id }));
        }}
      />
    </Layout>
  );
}